import Big from 'big.js';
import type {
  BillLimitDto,
  InhouseDigitalBillDetailsDto,
  InhouseDigitalBillOrderDetailsDto,
  InhouseDigitalBillOrderLineDetailsDto,
} from '@ordr-as/ordr-service-types/epos';
import getOrdersGroupedByName from '../pages/DigitalBill/unsettled/sidebar/details/OrderSummary/getOrdersGroupedByName';
import { getErrorCode } from './problemDetail';

export type OrderLineQuantityMap = Record<string, number>;

export const orderLinesFromDigitalBill = (
  digitalBill: InhouseDigitalBillDetailsDto
): InhouseDigitalBillOrderLineDetailsDto[] => digitalBill.orders.flatMap((order) => order.orderLines);

export const validOrdersFromDigitalBill = (
  digitalBill: InhouseDigitalBillDetailsDto
): InhouseDigitalBillOrderDetailsDto[] =>
  digitalBill.orders
    .map((order) => ({
      ...order,
      orderLines: order.orderLines.filter((ol) => ol.paymentStatus !== 'DISCARDED'),
    }))
    .filter((fo) => fo.orderLines.length !== 0);

export const orderLinesSelectedInSplitBill = (
  orderLines: InhouseDigitalBillOrderLineDetailsDto[],
  orderLineQuantityMap: OrderLineQuantityMap
): InhouseDigitalBillOrderLineDetailsDto[] =>
  orderLines.map((ol) => ({ ...ol, quantity: orderLineQuantityMap[ol.id] || 0 })).filter((ol) => ol.quantity !== 0);

export const quantityMapWithFullQuantityFromOrderLine = (
  orderLines: InhouseDigitalBillOrderLineDetailsDto[][]
): OrderLineQuantityMap => orderLines.flat().reduce((map, ol) => ({ [ol.id]: ol.quantity, ...map }), {});

export const quantityMapWithSelectedOrderLinesRemoved = (
  orderLineIdsToBeRemoved: number[],
  orderLineQuantityMap: OrderLineQuantityMap
): OrderLineQuantityMap => {
  const orderLineIdsToKeep = Object.keys(orderLineQuantityMap)
    .map((olId) => Number.parseInt(olId))
    .filter((olId) => !orderLineIdsToBeRemoved.includes(olId));

  return orderLineIdsToKeep.reduce((obj: OrderLineQuantityMap, key) => {
    obj[key] = orderLineQuantityMap[key]!;
    return obj;
  }, {});
};

export const validOrdersByName = (digitalBill: InhouseDigitalBillDetailsDto) => {
  const valid = validOrdersFromDigitalBill(digitalBill);

  return getOrdersGroupedByName(valid);
};

export const shouldDisplayPaymentReminder = (digitalBill: InhouseDigitalBillDetailsDto): boolean => {
  const bookingOwnerHasPhone =
    digitalBill.mainContactDetails?.phoneAvailable === true && digitalBill.mainContactDetails.userType === 'GUEST';

  const ordersHaveGuestPhone =
    digitalBill.orders.some(
      (order) => order.bookingDoneBy?.userType === 'GUEST' && Boolean(order.bookingDoneBy?.phoneNumber)
    ) ?? false;

  return (bookingOwnerHasPhone || ordersHaveGuestPhone) && digitalBill.unpaidSum > 0;
};

const DEFAULT_BILL_DETAILS_REFETCH_INTERVAL = 120_000;
// A pending bill limit change is resolved by the guest in Unibill; there is no
// push channel from ordr-service to ePOS, so poll tightly until it resolves.
const PENDING_BILL_LIMIT_REFETCH_INTERVAL = 5000;

export const billDetailsRefetchInterval = (digitalBill: InhouseDigitalBillDetailsDto | undefined): number =>
  digitalBill?.billLimit?.pendingChange ? PENDING_BILL_LIMIT_REFETCH_INTERVAL : DEFAULT_BILL_DETAILS_REFETCH_INTERVAL;

/** True when the bill has a limit and nothing remains (the server clamps remaining at 0). */
export const isBillLimitReached = (billLimit: BillLimitDto | undefined | null): boolean =>
  billLimit != null && (billLimit.remainingAmount ?? 0) <= 0;

/**
 * Proactive client-side check (R7). The server stays the authority — a race with
 * another device still yields a BILL_LIMIT_EXCEEDED rejection on submit.
 */
export const wouldExceedBillLimit = (cartTotal: number, billLimit: BillLimitDto | undefined | null): boolean => {
  const remainingAmount = billLimit?.remainingAmount;
  if (remainingAmount == null) {
    return false;
  }

  return new Big(cartTotal).gt(remainingAmount);
};

export const isBillLimitExceededError = (error: unknown): boolean => getErrorCode(error) === 'BILL_LIMIT_EXCEEDED';
